import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import Menu from "./Menu";
import InfoDot from "./InfoDot";
import GroupsControl, { type Scope } from "./ScopePicker";
import { useAnchored } from "../lib/anchored";
import { PageTitle } from "../lib/nav";
import PageBack from "./PageBack";

/**
 * Shared header slots — selective layout direction, October 28, 2026.
 * Identity and primary/company/Actions share the first band. Search and flags,
 * same-page lenses, then scope/Filters/View share the second band. Navigation
 * between pages stays in Shell's flyouts. This branch deliberately supersedes
 * the earlier single-band/hidden-title layout; see docs/selective-layout.md.
 *
 * The component owns placement and the measured toolbar fallback, so pages
 * supply named slots without composing separate toolbars. Answer is retained
 * as its own scrolling financial summary below the header. Filters stay over
 * the page; no financial behavior or permissions are decided here.
 */
export default function PageHeader({
  back = true,
  title,
  sub,
  info,
  scope,
  filters,
  scopeControl,
  search,
  flag,
  switch: switcher,
  view,
  company,
  primary,
  children,
  actions,
  answer,
}: {
  /** False only when a detail page already owns a contextual return trail. */
  back?: boolean;
  /** For a page outside a tabbed section, or a detail page: the H1 itself. */
  title?: ReactNode;
  /** The short line beside the title: a count, a state, a qualifier. NOT an
   *  explanation of what the page is — that belongs in `info`. */
  sub?: ReactNode;
  /** The one explanation a page needs, tucked behind a circled-i beside the
   *  title. For a genuine caveat, not a description of the obvious. */
  info?: ReactNode;
  scopeControl?: ReactNode;
  scope?: { scope: Scope; onChange: (next: Scope) => void; onGroupsChanged?: () => void };
  filters?: {
    /** How many are set, shown on the button so a narrow result explains itself. */
    count: number;
    children: ReactNode;
    /** Something under the fields -- Apply / Clear on a page that searches on demand. */
    foot?: ReactNode;
  };
  /** The page's search box or search form. Fixed width. */
  search?: ReactNode;
  /**
   * A short status flag that belongs beside the search on the LEFT at full
   * prominence — a page-level alert ("6 without a password") or a left-hand
   * link ("Enter balances by hand →"). Unlike `sub` it is not muted, and unlike
   * a block below the header it keeps the header to one row (Jonathan,
   * 2026-10-22: these must share the header line, not stack under it).
   */
  flag?: ReactNode;
  /** The page's own switch (segment / group tabs / lens / file picker / tabs). */
  switch?: ReactNode;
  /** The Cards/List toggle. */
  view?: ReactNode;
  /**
   * The company this report/drill-down is about: a top-right selector ("CBY ▾"),
   * the same control on every Books page (round-3 #3). It is its own slot so the
   * company never drifts into the centre toggle again, and so "which company am
   * I looking at, and how do I switch" is answered the same way everywhere.
   */
  company?: ReactNode;
  /** The page's main button(s). */
  primary?: ReactNode;
  /** Legacy free-form slot; lands in the switch position. */
  children?: ReactNode;
  actions?: ReactNode;
  /**
   * The answer to the one question this page asks (web/DESIGN.md §5): the
   * biggest figure on the page. Drawn under the header bar, never inside it.
   */
  answer?: { label: string; value: ReactNode; note?: ReactNode };
}) {
  // Every section has one visible H1; record-specific JSX can supply its own.
  const titleIsName = typeof title === "string";

  // Fit the toolbar as one deliberate band, then use the shared wrap mode
  // before its slot contents collide. The title/actions band is independent.
  const headRef = useRef<HTMLDivElement>(null);
  // Re-measure after every render, since content (a count, a switch label)
  // changes the widths. Sets an attribute, not state, so it cannot loop.
  useLayoutEffect(() => {
    if (headRef.current) applyHeaderFit(headRef.current);
  });
  // And on every width change of the header itself.
  useLayoutEffect(() => {
    const head = headRef.current;
    if (!head) return;
    let lastWidth = head.clientWidth;
    const ro = new ResizeObserver(() => {
      if (head.clientWidth === lastWidth) return;
      lastWidth = head.clientWidth;
      applyHeaderFit(head);
    });
    ro.observe(head);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div className="page-head" ref={headRef}>
        <div className="head-top">
          <div className="head-context">
            {back ? <PageBack standalone /> : null}
            <div className="head-identity">
              {titleIsName ? <h1 className="page-h1">{title}</h1> : title !== undefined ? title : <PageTitle className="page-h1" />}
              {sub ? <span className="page-sub">{sub}</span> : null}
              {info ? <InfoDot>{info}</InfoDot> : null}
            </div>
          </div>
          <div className="head-actions no-print">
            {primary}
            {company}
            {actions ? <Menu label="Actions">{actions}</Menu> : null}
          </div>
        </div>
        {search || flag || switcher || children || scopeControl || scope || filters || view ? <div className="head-toolbar no-print">
          <div className="head-left">{search}{flag}</div>
          <div className="head-center">{switcher}{children}</div>
          <div className="inline-actions head-controls">
            {scopeControl}
            {scope ? <GroupsControl scope={scope.scope} onChange={scope.onChange} onGroupsChanged={scope.onGroupsChanged} /> : null}
            {filters ? <Filters {...filters} /> : null}
            {view}
          </div>
        </div> : null}
      </div>
      {answer ? (
        <div className="page-answer">
          <span className="page-answer-label">{answer.label}</span>
          <span className="page-answer-value">{answer.value}</span>
          {answer.note ? <span className="page-answer-note">{answer.note}</span> : null}
        </div>
      ) : null}
    </>
  );
}

/** Measure the toolbar above the shared tablet fallback. */
function applyHeaderFit(head: HTMLElement) {
  if (!window.matchMedia("(min-width: 1200px)").matches) {
    delete head.dataset.fit;
    return;
  }
  head.dataset.fit = "row";
  head.dataset.fit = fitsOneRow(head) ? "row" : "stacked";
}

function fitsOneRow(head: HTMLElement): boolean {
  const band = head.querySelector<HTMLElement>(".head-toolbar");
  if (!band) return true;
  const zones = [".head-left", ".head-center", ".head-controls"].map(selector => head.querySelector<HTMLElement>(selector));
  const occupied = zones.filter((zone): zone is HTMLElement => !!zone && zone.children.length > 0);
  const gap = parseFloat(getComputedStyle(band).columnGap) || 0;
  return occupied.reduce((sum, zone) => sum + natural(zone), 0) + Math.max(0, occupied.length - 1) * gap <= band.clientWidth;
}

/** The width a zone needs for its children side by side, ignoring its box. */
function natural(zone: HTMLElement): number {
  const kids = [...zone.children] as HTMLElement[];
  const gap = parseFloat(getComputedStyle(zone).columnGap) || 0;
  const widths = kids.reduce((sum, k) => sum + k.getBoundingClientRect().width, 0);
  return widths + gap * Math.max(0, kids.length - 1);
}

/** The Filters button and the panel it opens over the page. */
function Filters({
  count,
  children,
  foot,
}: {
  count: number;
  children: ReactNode;
  foot?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { anchor, panel, style } = useAnchored<HTMLButtonElement>(open, () => setOpen(false), "right");
  return (
    <>
      <button
        type="button"
        ref={anchor}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={count > 0 ? "filters-on" : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        Filters
        {count > 0 ? <span className="pill count">{count}</span> : null}
      </button>
      {open ? (
        <div ref={panel} className="filter-pop no-print" role="dialog" aria-label="Filters" style={style}>
          <div className="filter-fields">{children}</div>
          {foot ? (
            <div
              className="filter-foot"
              // Clicking Apply/Done closes the panel (Jonathan: "as soon as you
              // click apply, the modal should close"). Clear/Reset keeps it open
              // so you can keep adjusting. The page's own handler runs first;
              // this just closes after it on the bubble.
              onClick={(e) => {
                const btn = (e.target as HTMLElement).closest("button");
                if (btn && /appl|done/i.test(btn.textContent ?? "")) {
                  setOpen(false);
                }
              }}
            >
              {foot}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** One labelled control inside the Filters panel. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="filter-field">
      <span className="k">{label}</span>
      {children}
    </label>
  );
}
